/**
 * Correct a student's name — PRD §2.1 ("Create/edit/archive students").
 *
 * Name and surname only. A typo at the front desk is the reason this exists;
 * date of birth and height are not typos people notice later, and weight is
 * the latest measurement, which is corrected on the Ölçümler tab where it
 * leaves a dated record.
 *
 * The API takes the whole profile on PATCH, so the fields this dialog does not
 * show travel unchanged.
 */

import { useEffect, useId, useState } from 'react';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import type { Gender } from '../api/types.js';
import { Button } from './ui/Button.js';
import { Field } from './ui/Field.js';
import './StudentNameDialog.css';

interface StudentNameDialogProps {
  student: {
    id: string;
    firstName: string;
    lastName: string;
    gender: Gender;
    dateOfBirth: string;
    heightCm: number;
  };
  onCancel: () => void;
  onSaved: () => void;
}

export function StudentNameDialog({
  student,
  onCancel,
  onSaved,
}: StudentNameDialogProps) {
  const titleId = useId();

  const [firstName, setFirstName] = useState(student.firstName);
  const [lastName, setLastName] = useState(student.lastName);

  const [error, setError] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel, busy]);

  const incomplete = firstName.trim() === '' || lastName.trim() === '';
  const unchanged =
    firstName.trim() === student.firstName && lastName.trim() === student.lastName;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    setFieldErrors({});

    try {
      await api.patch(`/api/students/${student.id}`, {
        firstName,
        lastName,
        gender: student.gender,
        dateOfBirth: student.dateOfBirth,
        heightCm: student.heightCm,
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(messageFor(err.code));
        const fields = err.details?.fields;
        if (Array.isArray(fields)) {
          const next: Record<string, string> = {};
          for (const field of fields) {
            if (typeof field === 'string') next[field] = 'Bu alanı kontrol edin.';
          }
          setFieldErrors(next);
        }
      } else {
        setError(messageFor('INTERNAL_ERROR'));
      }
      // What was typed stays: a rejected save must not cost the edit (PRD §18).
      setBusy(false);
    }
  }

  return (
    <div className="sname__backdrop" role="presentation">
      <form
        className="sname__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={submit}
        noValidate
      >
        <h2 className="sname__title" id={titleId}>
          İsmi Düzenle
        </h2>

        <div className="sname__row">
          <Field
            label="Ad"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={fieldErrors.firstName}
            autoComplete="off"
            maxLength={60}
            autoFocus
            required
          />
          <Field
            label="Soyad"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={fieldErrors.lastName}
            autoComplete="off"
            maxLength={60}
            required
          />
        </div>

        <p className="sname__note">
          Daha önce oluşturulmuş raporlar değişmez, eski isimle kalır. Bundan sonra
          oluşturacağınız raporlarda yeni isim görünür.
        </p>

        {error && (
          <p className="sname__error" role="alert">
            {error}
          </p>
        )}

        <div className="sname__actions">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
            Vazgeç
          </Button>
          <Button type="submit" loading={busy} disabled={incomplete || unchanged}>
            {busy ? 'Kaydediliyor…' : 'Kaydet'}
          </Button>
        </div>
      </form>
    </div>
  );
}
